import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import API from '../../api/API';
import { urlEdizione } from './links';
// Caricamento ed errore hanno già le loro regole nel foglio della sezione.
import './Statistiche.css';
import './Indici.css';

/**
 * L'unico indice che non ha bisogno di niente di nuovo lato server: api/statistiche/tornei già
 * restituisce i circuiti con le loro sole edizioni pubbliche.
 *
 * La prima stesura era un elenco verticale di date, una per riga: trenta circuiti e centonove
 * edizioni diventavano una colonna lunghissima in un sesto dello schermo, con come etichetta
 * principale "24/05/2025". Ma a un torneo non si pensa per data: si pensa "Eagles, 2024".
 * Qui ogni circuito è un riquadro e le sue edizioni sono anni da toccare, quindi la pagina si
 * legge in un colpo d'occhio e dice anche una cosa che prima non diceva — da quanti anni un
 * torneo esiste.
 */

/** Il segnaposto che il database usa quando l'edizione non ha un nome. */
const SENZA_NOME = '_';

const anno = (data) => new Date(data).getFullYear();

/**
 * L'etichetta di una edizione è l'anno, e si allunga solo quando serve a distinguerla.
 *
 * Con due edizioni nello stesso anno l'anno da solo non basta e si aggiunge il mese; con due
 * nello stesso mese — succede, "GV brescia" ne ha due a settembre 2023 — non basta nemmeno
 * quello e si arriva al giorno. Ovunque non serva la pastiglia resta corta come deve: due
 * pastiglie identiche sono peggio di una lunga, perché non si sa quale toccare.
 */
const etichetta = (edizione, ripetuti) => {
  const data = new Date(edizione.data);
  const a = anno(edizione.data);
  if (!ripetuti.anni.has(a)) return String(a);

  const mese = data.toLocaleDateString('it-IT', { month: 'short' });
  if (!ripetuti.mesi.has(`${a}-${data.getMonth()}`)) return `${mese} ${a}`;

  return `${data.getDate()} ${mese} ${a}`;
};

const descrizione = (edizione) => {
  const data = new Date(edizione.data).toLocaleDateString('it-IT');
  const nome = edizione.edizione && edizione.edizione !== SENZA_NOME ? ` · ${edizione.edizione}` : '';
  return `${data}${nome}`;
};

/** I circuiti con più edizioni per primi: sono i tornei che esistono davvero da anni. */
const perRilievo = (a, b) =>
  b.edizioni.length - a.edizioni.length || a.nome.localeCompare(b.nome, 'it', { sensitivity: 'base' });

export default function IndiceEdizioni() {
  const [circuiti, setCircuiti] = useState([]);
  const [filtro, setFiltro] = useState('');
  const [caricando, setCaricando] = useState(true);

  useEffect(() => {
    let vivo = true;
    API.statistiche.tornei()
      .then((r) => { if (vivo && r.isSuccess) setCircuiti(r.data || []); })
      .catch(() => { /* l'elenco vuoto lo dice già da sé */ })
      .finally(() => { if (vivo) setCaricando(false); });
    return () => { vivo = false; };
  }, []);

  const visibili = useMemo(() => {
    const q = filtro.trim().toLowerCase();
    return [...circuiti]
      .filter((c) => !q || c.nome.toLowerCase().includes(q))
      .map((c) => {
        const edizioni = [...(c.edizioni || [])].sort((x, y) => new Date(y.data) - new Date(x.data));
        const vistiAnni = new Set();
        const vistiMesi = new Set();
        const ripetuti = { anni: new Set(), mesi: new Set() };
        edizioni.forEach((e) => {
          const a = anno(e.data);
          const m = `${a}-${new Date(e.data).getMonth()}`;
          if (vistiAnni.has(a)) ripetuti.anni.add(a);
          if (vistiMesi.has(m)) ripetuti.mesi.add(m);
          vistiAnni.add(a);
          vistiMesi.add(m);
        });
        return { ...c, edizioni, ripetuti };
      })
      .sort(perRilievo);
  }, [circuiti, filtro]);

  const totale = circuiti.reduce((n, c) => n + (c.edizioni?.length || 0), 0);

  if (caricando) return <div className="statistiche-loading">Caricamento…</div>;

  return (
    <>
      <h1 className="indice-titolo">Edizioni</h1>

      <div className="indice-filtri">
        <input
          type="search"
          className="indice-filtro"
          aria-label="Filtra i tornei"
          placeholder="Filtra per nome del torneo"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
      </div>

      <p className="indice-conteggio">
        {totale} edizioni in {circuiti.length} torne{circuiti.length === 1 ? 'o' : 'i'}
      </p>

      {visibili.length === 0
        ? <div className="tab-ord-vuoto">Nessun torneo corrisponde al filtro</div>
        : (
          <div className="circuiti-griglia">
            {visibili.map((c) => (
              <section className="circuito" key={c.nome}>
                <header className="circuito-testa">
                  <h2 className="circuito-nome">{c.nome}</h2>
                  <span className="circuito-conto">
                    {c.edizioni.length} edizion{c.edizioni.length === 1 ? 'e' : 'i'}
                  </span>
                </header>
                <ul className="circuito-anni">
                  {c.edizioni.map((e) => (
                    <li key={e.id}>
                      <Link className="anno-pastiglia" to={urlEdizione(e.id)} title={descrizione(e)}>
                        {etichetta(e, c.ripetuti)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
    </>
  );
}
